/**
 * Containment-aware overlap suppression for image-measurement traits that opt in
 * (GLE-79). Which traits those are is per-trait config, not a crop list in code —
 * see {@link ContainmentSuppressionOptions}. Web mirror of the iOS implementation:
 *   - Glean/Utils/ContainmentOverlapFilter.swift
 *   - greedyConfidenceSuppressionToIndices in Glean/Utils/ImageUtils.swift
 *
 * Why: plain IoU NMS structurally cannot see containment — a low-confidence
 * "ghost" detection sitting on part of an already-counted object (or spanning
 * several) keeps IoU below the slider threshold and gets counted. Suppression
 * here fires on bbox IoMin (intersection over minimum area) confirmed by mask
 * overlap, always dropping the lower-confidence member of a redundant pair.
 * The mask confirmation protects real objects whose bounding boxes nest
 * (curved fruit "spooning", occluded fruit) — their masks stay disjoint even
 * when their boxes contain one another.
 *
 * Candidacy rules live in the callers (viewer, deferred pipeline, parity
 * harness) — each has its own pre-filter. This module only decides, for the
 * candidates given, who survives the overlap stage. Suppressed detections are
 * persisted as the existing `overlapTooHigh` hidden reason on both platforms;
 * the suppressedIoU/suppressedContainment distinction is in-memory only (it is
 * asserted per-detection by the cross-platform parity fixture).
 *
 * Cross-platform determinism contract (pinned on iOS too): visit order is
 * confidence DESCENDING with INPUT-INDEX tie-break. Without the tie-break,
 * equal-confidence overlapping boxes can produce platform-divergent keep sets.
 */

import type { BBoxPixels } from "./types/annotations.js";

export interface OverlapDetection {
  bbox?: BBoxPixels;
  confidence?: number;
  /**
   * The detection's model class (see {@link resolveDetectionClassLabel}).
   * Compared case-insensitively after trimming, so callers may pass it raw.
   * Only read when the containment params are `classAware`; ignored otherwise.
   * Detections with no label form one shared bucket, so they suppress each other
   * but never a labelled detection.
   */
  classLabel?: string;
  /**
   * Lazy decode of the detection's bbox-cropped binary mask ([row][col],
   * true = foreground). The mask grid is defined to span the bbox rect, so
   * mask resolution is arbitrary (server masks are bbox crops at analysis
   * resolution, not image resolution). Only called once a candidate passes
   * the frames-only IoMin screen; callers should memoize decodes.
   * null = unavailable.
   */
  getMask?: () => boolean[][] | null;
}

export type OverlapOutcome = "kept" | "suppressedIoU" | "suppressedContainment";

export interface ContainmentParams {
  /** Stage 1 screen: candidate is a containment suspect only if
   *  max IoMin(candidate, kept) >= this. */
  ioMinThreshold: number;
  /** Stage 2 confirmation: fraction of the candidate's mask foreground covered
   *  by the union of kept masks must be >= this to suppress. */
  maskCoverageThreshold: number;
  /**
   * When true, containment only suppresses a candidate against kept detections
   * carrying the SAME class label. Set for open-set detectors, whose prompts can
   * legitimately name a part and its whole ("Hesperaloe Leaf" inside "Hesperaloe
   * Plant") or two classes of the same object (a relabel task prompted
   * ["lettuce", "bad plant"]).
   *
   * False for every fixed-model crop, because their class label is NOT an object
   * identity: `replace_classes_with_color_based_maturity` in the image processor
   * overwrites it with "ripe" / "unripe" for tomato traits, so comparing classes
   * there would let an unripe ghost survive inside a real ripe tomato — the exact
   * duplicate this filter exists to remove. Those models detect a single object
   * type anyway, so class-blind is both correct and simpler.
   *
   * The plain-IoU stage is class-blind regardless: two near-identical boxes are
   * the same physical object even when two prompts disagree about its name.
   */
  classAware: boolean;
}

/**
 * Per-trait containment configuration, stored on the entry config at
 * `objectDetectionDefaultOptions.containmentSuppression`.
 *
 * Presence of this object ENABLES the filter for the trait; absence leaves the
 * trait on plain-IoU NMS alone. Containment runs as part of the overlap stage,
 * after the confidence / distance / length filters, so only surviving detections
 * compete.
 */
export interface ContainmentSuppressionOptions {
  /**
   * Stage 1 — the containment SCREEN.
   *
   * IoMin ("intersection over minimum area") is the fraction of the SMALLER box
   * swallowed by the larger one. It reaches 1.0 at full containment no matter how
   * different the two boxes are in size — which is exactly the signal plain IoU
   * cannot see, because IoU divides by the union, so a small box inside a large
   * one scores low and survives NMS at any sane threshold.
   *
   * A detection becomes a containment suspect only when its IoMin against an
   * already-kept box reaches this value. Raise it to suppress LESS (only
   * near-total nesting counts); lower it to suppress MORE (partial overlap starts
   * counting). Boxes failing this screen never have their masks decoded, so this
   * also bounds the cost of the filter.
   *
   * Default {@link DEFAULT_CONTAINMENT_PARAMS}.ioMinThreshold (0.8) — the midpoint
   * of the measured safe window on the cucumber bucket-of-60 golden dataset
   * (real-vs-real IoMin ceiling 0.60, ghost floor 0.96).
   */
  ioMinThreshold?: number;
  /**
   * Stage 2 — the mask CONFIRMATION.
   *
   * Of the suspect's own mask foreground pixels, the fraction covered by the UNION
   * of the kept detections' masks. This is what protects REAL objects whose
   * bounding boxes nest but whose pixels do not: curved fruit "spooning", one head
   * tucked behind another, a plant overlapping its neighbour. Their boxes may
   * contain one another, but their masks stay disjoint, so coverage stays low and
   * both survive. The union formulation catches both ghost shapes with one rule —
   * a part-detection (a subset of one real object) and an envelope spanning
   * several of them.
   *
   * The suspect is dropped only when coverage reaches this value. Raise it to
   * suppress LESS (demands near-total pixel overlap); lower it to suppress MORE.
   * A candidate whose mask cannot be decoded is never suppressed — containment
   * requires positive confirmation.
   *
   * Default {@link DEFAULT_CONTAINMENT_PARAMS}.maskCoverageThreshold (0.8).
   */
  maskCoverageThreshold?: number;
}

/**
 * The `objectDetectionDefaultOptions` map on an image-measurement trait: optional
 * guardrails applied to object-detection models when a per-model value is not set.
 *
 * Declared HERE, next to the containment options it carries, and re-exported by
 * `shared/constants/systemTraits.ts` and `src/types/entryConfig.ts` so all three
 * layers share one declaration. This is a live Firestore map mirrored on iOS —
 * with separate copies, structural typing keeps them mutually assignable while
 * they silently disagree about what a trait can express, and nothing fails.
 *
 * Only `containmentSuppression` is read by this module.
 */
export interface ObjectDetectionDefaultOptions {
  defaultModelConfidenceThreshold?: number;
  maxObjectLengthMeters?: number;
  minObjectLengthMeters?: number;
  /**
   * Opt in to containment-aware overlap suppression: drop a detection that sits
   * (almost) entirely inside an already-kept one, keeping the higher-confidence
   * box. ABSENT = DISABLED, so traits authored before this feature are unchanged.
   *
   * See {@link ContainmentSuppressionOptions} for what each threshold does and
   * which direction tightens it. Mirrored on iOS as
   * `ObjectDetectionDefaultOptions.containmentSuppression`.
   */
  containmentSuppression?: ContainmentSuppressionOptions;
}

/**
 * Thresholds applied when a trait enables containment without overriding them.
 * Measured safe window on the cucumber bucket-of-60 golden dataset: real-vs-real
 * bbox IoMin ceiling 0.60, ghost floor 0.96; 0.8 is the midpoint. Mirrors
 * FilterConstants.containment* on iOS.
 */
export const DEFAULT_CONTAINMENT_PARAMS: ContainmentParams = {
  ioMinThreshold: 0.8,
  maskCoverageThreshold: 0.8,
  classAware: false,
};

/** Open-set is the only model kind whose class label encodes object identity. */
const CLASS_AWARE_MODEL_KIND = "openSetObjectDetector";

function clampThreshold(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : fallback;
}

/**
 * Single source of the "does this trait get containment, with which params"
 * policy, shared by the viewer and the deferred pipeline so a future crop
 * adoption cannot silently diverge between layers. Mirrors the iOS gate
 * (CapturedImageViewController.containmentFilterConfig).
 *
 * Absence of `containmentSuppression` means DISABLED — every trait that predates
 * this feature keeps plain-IoU NMS and behaves exactly as it did before.
 */
export function resolveContainmentParams(
  detectionDefaults: ObjectDetectionDefaultOptions | undefined,
  modelKind: string | undefined
): ContainmentParams | undefined {
  const options = detectionDefaults?.containmentSuppression;
  if (!options) return undefined;

  return {
    ioMinThreshold: clampThreshold(options.ioMinThreshold, DEFAULT_CONTAINMENT_PARAMS.ioMinThreshold),
    maskCoverageThreshold: clampThreshold(
      options.maskCoverageThreshold,
      DEFAULT_CONTAINMENT_PARAMS.maskCoverageThreshold
    ),
    classAware: modelKind === CLASS_AWARE_MODEL_KIND,
  };
}

/**
 * The detection's model class, normalized for comparison (trimmed, lowercased).
 *
 * Reads `attributes.class_label` — the one field BOTH producers write: iOS from
 * `boxData.classLabel` (CapturedImageView) and the deferred Cloud Function from
 * the processor response's `maturity` field. `category.name` is deliberately only
 * a fallback for metadata written before `class_label` existed: the Cloud
 * Function stamps every box's `category.name` with the trait's FIRST prompt, so
 * on that path it identifies the trait, not the detection.
 */
export function resolveDetectionClassLabel(annotation: {
  attributes?: Record<string, unknown> | null;
  category?: { name?: string | null } | null;
}): string | undefined {
  return normalizeClassLabel(annotation.attributes?.class_label) ?? normalizeClassLabel(annotation.category?.name);
}

/** Trimmed + lowercased, or undefined for anything unusable. */
function normalizeClassLabel(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  return normalized || undefined;
}

function bboxArea(b: BBoxPixels): number {
  return b.width * b.height;
}

function intersectionArea(a: BBoxPixels, b: BBoxPixels): number {
  const width = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const height = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return width > 0 && height > 0 ? width * height : 0;
}

/** Plain intersection-over-union. Same math as the viewer's computeIoU and the
 *  server's bboxIou (kept local so this module stays dependency-free). */
function computeIoU(a: BBoxPixels, b: BBoxPixels): number {
  const intersection = intersectionArea(a, b);
  if (intersection === 0) return 0;
  const union = bboxArea(a) + bboxArea(b) - intersection;
  return union > 0 ? intersection / union : 0;
}

/**
 * Intersection over Minimum area: the fraction of the SMALLER box swallowed by
 * the larger one — 1.0 at full containment regardless of size ratio, which is
 * the containment signal plain IoU structurally cannot see. Mirrors
 * computeIoMin in ImageUtils.swift.
 */
export function computeIoMin(a: BBoxPixels, b: BBoxPixels): number {
  const intersection = intersectionArea(a, b);
  if (intersection === 0) return 0;
  const minArea = Math.min(bboxArea(a), bboxArea(b));
  return minArea > 0 ? intersection / minArea : 0;
}

interface MaskedDetection {
  bbox: BBoxPixels;
  mask: boolean[][] | null;
}

/**
 * Fraction of the candidate's foreground pixels covered by the UNION of the
 * kept detections' masks. The union formulation catches both ghost shapes with
 * one rule: a part-detection (subset of one real's mask) and an envelope
 * spanning several reals (covered only by their union).
 *
 * Iterates the candidate's own mask grid — never materializes full-image
 * bitmaps. Each foreground pixel-center maps through the bbox rects into each
 * intersecting keeper's grid. A keeper with no mask counts as a solid rect
 * (bbox-only semantics). Returns null when the candidate mask is null/empty or
 * has zero foreground pixels. Mirrors maskUnionCoverageFraction in
 * ContainmentOverlapFilter.swift.
 */
function maskUnionCoverageFraction(candidate: MaskedDetection, kept: MaskedDetection[]): number | null {
  const candidateMask = candidate.mask;
  if (!candidateMask || candidateMask.length === 0 || candidateMask[0].length === 0) return null;

  const rows = candidateMask.length;
  const cols = candidateMask[0].length;
  const frame = candidate.bbox;
  const intersectingKept = kept.filter((keeper) => intersectionArea(keeper.bbox, frame) > 0);

  let foregroundCount = 0;
  let coveredCount = 0;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (!candidateMask[row][col]) continue;
      foregroundCount += 1;
      const pointX = frame.x + ((col + 0.5) / cols) * frame.width;
      const pointY = frame.y + ((row + 0.5) / rows) * frame.height;

      for (const keeper of intersectingKept) {
        // Half-open on the far edges, mirroring iOS CGRect.contains(point).
        const inKeeperFrame =
          pointX >= keeper.bbox.x &&
          pointX < keeper.bbox.x + keeper.bbox.width &&
          pointY >= keeper.bbox.y &&
          pointY < keeper.bbox.y + keeper.bbox.height;
        if (!inKeeperFrame) continue;

        const keeperMask = keeper.mask;
        if (!keeperMask) {
          coveredCount += 1; // no mask = solid rect
          break;
        }
        const keeperRows = keeperMask.length;
        const keeperCols = keeperMask[0]?.length ?? 0;
        if (keeperRows === 0 || keeperCols === 0) continue;

        const colIndex = Math.floor(((pointX - keeper.bbox.x) / keeper.bbox.width) * keeperCols);
        const rowIndex = Math.floor(((pointY - keeper.bbox.y) / keeper.bbox.height) * keeperRows);
        const clampedRow = Math.min(Math.max(rowIndex, 0), keeperRows - 1);
        const clampedCol = Math.min(Math.max(colIndex, 0), keeperCols - 1);
        if (keeperMask[clampedRow][clampedCol]) {
          coveredCount += 1;
          break;
        }
      }
    }
  }

  return foregroundCount === 0 ? null : coveredCount / foregroundCount;
}

/**
 * Containment verdict for one candidate against the kept set.
 *
 * Stage 1: bbox IoMin against every kept box — below threshold means the
 * candidate is not even a containment suspect; masks are never consulted.
 * Stage 2: fraction of the candidate's mask covered by the union of kept
 * masks. A missing/degenerate candidate mask means we CANNOT confirm
 * containment, so the candidate is kept — the filter only ever suppresses on
 * positive evidence. (The cucumber dataset this was validated on never exercised
 * that path: all 823 golden detections carry a mask. Now that non-cucumber crops
 * are eligible, guessing wrong here would delete a real measurement.)
 * Mirrors shouldSuppressByContainment in ContainmentOverlapFilter.swift.
 */
function shouldSuppressByContainment(
  candidate: MaskedDetection,
  kept: MaskedDetection[],
  params: ContainmentParams
): boolean {
  // "Contained by nothing" is not containment. Structural, not threshold-based:
  // with a misconfigured `ioMinThreshold: 0` every `maxIoMin >= 0` comparison is
  // true, and an empty kept set yields coverage 0, which `maskCoverageThreshold:
  // 0` would also clear — suppressing detections that overlap nothing at all.
  // The filter must never be able to empty an image, whatever the config says.
  if (kept.length === 0) return false;

  const maxIoMin = kept.reduce((acc, keeper) => Math.max(acc, computeIoMin(candidate.bbox, keeper.bbox)), 0);
  if (maxIoMin < params.ioMinThreshold) return false;

  const coverage = maskUnionCoverageFraction(candidate, kept);
  if (coverage === null) return false;
  return coverage >= params.maskCoverageThreshold;
}

/**
 * Full overlap pass: the plain-IoU check (slider threshold, unchanged
 * semantics) PLUS, when `containment` is provided, the fixed containment check
 * — in one greedy confidence-ordered walk. Detections lacking bbox or
 * confidence are never kept (outcome undefined), matching iOS.
 */
export function evaluateOverlapSuppression(
  detections: OverlapDetection[],
  iouThreshold: number,
  containment?: ContainmentParams
): { keep: boolean[]; outcomes: (OverlapOutcome | undefined)[] } {
  const keep: boolean[] = new Array(detections.length).fill(false);
  const outcomes: (OverlapOutcome | undefined)[] = new Array(detections.length).fill(undefined);

  // `typeof === "number"` (not `!== undefined`) so a JSON null confidence is
  // dropped, matching iOS's `guard let confidence` nil-drop and the deferred
  // pipeline's candidate filter — keeps the cross-platform contract exact.
  const validIndices = detections
    .map((detection, index) => (detection.bbox && typeof detection.confidence === "number" ? index : -1))
    .filter((index) => index !== -1);

  // Descending confidence with input-index tie-break — the cross-platform
  // determinism contract (see module doc).
  const sortedIndices = [...validIndices].sort((lhs, rhs) => {
    const lhsConfidence = detections[lhs].confidence ?? 0;
    const rhsConfidence = detections[rhs].confidence ?? 0;
    return lhsConfidence !== rhsConfidence ? rhsConfidence - lhsConfidence : lhs - rhs;
  });

  const maskByIndex = new Map<number, boolean[][] | null>();
  const maskedDetection = (index: number): MaskedDetection => {
    if (!maskByIndex.has(index)) {
      maskByIndex.set(index, detections[index].getMask?.() ?? null);
    }
    return { bbox: detections[index].bbox!, mask: maskByIndex.get(index)! };
  };

  // Normalized ONCE per detection, not once per (candidate, kept) pair — the walk
  // is O(n²), so re-trimming inside it would cost ~n²/2 string scans on an image
  // with hundreds of detections. undefined when the trait is class-blind, which
  // also skips the work entirely for every fixed-model crop.
  const classByIndex = containment?.classAware
    ? detections.map((detection) => normalizeClassLabel(detection.classLabel))
    : undefined;
  // Only consulted by the containment stage — the IoU stage stays class-blind
  // (two near-identical boxes are one object even when two prompts disagree
  // about its name).
  const isContainmentPeer = (candidateIndex: number, keptIndex: number): boolean =>
    !classByIndex || classByIndex[candidateIndex] === classByIndex[keptIndex];

  const keptIndices: number[] = [];
  for (const candidateIndex of sortedIndices) {
    const candidateBox = detections[candidateIndex].bbox!;

    // One frames-only pass over the kept set: the existing slider-controlled
    // IoU suppression (unchanged), with the bbox IoMin screen piggybacked on
    // the same pairs so the common no-suspect case never decodes masks.
    let suppressed = false;
    let maxIoMin = 0;
    for (const keptIndex of keptIndices) {
      const keptBox = detections[keptIndex].bbox!;
      if (computeIoU(keptBox, candidateBox) > iouThreshold) {
        outcomes[candidateIndex] = "suppressedIoU";
        suppressed = true;
        break;
      }
      if (containment && isContainmentPeer(candidateIndex, keptIndex)) {
        maxIoMin = Math.max(maxIoMin, computeIoMin(keptBox, candidateBox));
      }
    }

    if (!suppressed && containment && maxIoMin >= containment.ioMinThreshold) {
      const candidate = maskedDetection(candidateIndex);
      // Only bbox-intersecting keepers of the same containment class can
      // contribute mask coverage (maskUnionCoverageFraction filters to
      // intersecting ones anyway) — pre-filtering here avoids decoding the masks
      // of far-away or different-class keepers entirely.
      const kept = keptIndices
        .filter(
          (keptIndex) =>
            isContainmentPeer(candidateIndex, keptIndex) &&
            intersectionArea(detections[keptIndex].bbox!, candidate.bbox) > 0
        )
        .map(maskedDetection);
      if (shouldSuppressByContainment(candidate, kept, containment)) {
        outcomes[candidateIndex] = "suppressedContainment";
        suppressed = true;
      }
    }

    if (!suppressed) {
      keep[candidateIndex] = true;
      outcomes[candidateIndex] = "kept";
      keptIndices.push(candidateIndex);
    }
  }

  return { keep, outcomes };
}
